<template>
  <div>
    <div v-if="responseCard.subTitle || responseCard.subtitle || responseCard.imageUrl" class="response-card-container">
      <div v-if="responseCard.subTitle">
        <span>{{responseCard.subTitle}}</span>
      </div>
      <div v-if="responseCard.subtitle">
        <span>{{responseCard.subtitle}}</span>
      </div>
      <v-img
        v-if="responseCard.imageUrl"
        :src="responseCard.imageUrl"
        contain
        height="33vh"
        class="response-card-image"
      />
    </div>
    <div v-if="responseCard.buttons" class="response-button-container">
      <v-btn
        v-for="(button) in responseCard.buttons"
        v-show="button.text && button.value"
        :key="button.id"
        :disabled="shouldDisableClickedResponseCardButtons"
        class="response-button"
        variant="flat"
        @click.once.native="onButtonClick(button.value)"
      >
        {{button.text}}
      </v-btn>
    </div>
    <div v-if="responseCard.attachmentLinkUrl">
      <v-btn
        variant="flat"
        class="response-link-button"
        tag="a"
        :href="responseCard.attachmentLinkUrl"
        target="_blank"
      >
        Open Link
      </v-btn>
    </div>
  </div>
</template>

<script>
/*
Copyright 2017-2019 Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Amazon Software License (the "License"). You may not use this file
except in compliance with the License. A copy of the License is located at

http://aws.amazon.com/asl/

or in the "license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. See the
License for the specific language governing permissions and limitations under the License.
*/
export default {
  name: 'response-card',
  props: ['response-card', 'message-id'],
  data() {
    return {
      hasButtonBeenClicked: false,
    };
  },
  computed: {
    shouldDisplayResponseCardTitle() {
      return this.$store.state.config.ui.shouldDisplayResponseCardTitle;
    },
    shouldDisableClickedResponseCardButtons() {
      // If this is the first message in the conversation (after clearing chat),
      // always enable the buttons regardless of previous state
      if (this.messageId === '0' || parseInt(this.messageId, 10) === 0) {
        return false;
      }
      
      return (
        this.$store.state.config.ui.shouldDisableClickedResponseCardButtons &&
        (this.hasButtonBeenClicked || this.getRCButtonsDisabled())
      );
    },
  },
  inject: ['getRCButtonsDisabled', 'setRCButtonsDisabled', 'resetRCButtonsDisabled'],
  watch: {
    '$store.state.buttonStateReset': function() {
      // Only reset buttons for the last message in the conversation
      if (this.messageId && parseInt(this.messageId, 10) === this.$store.state.messages.length - 1) {
        this.hasButtonBeenClicked = false;
        this.resetRCButtonsDisabled();
      }
    }
  },
  methods: {
    onButtonClick(value) {
      this.hasButtonBeenClicked = true;
      this.setRCButtonsDisabled();
      const message = {
        type: 'button',
        text: value,
      };

      this.$store.dispatch('postTextMessage', message);
    },
  },
};
</script>

<style scoped>
.response-card-container {
  width: 100%;
  max-width: 900px;
  margin-bottom: 8px;
  border-radius: 12px;
  overflow: hidden;
  background-color: #f5f5f5 !important;
  border: 1px solid #e0e0e0;
  transition: transform 0.2s ease;
  box-sizing: border-box;
  padding: 12px;
}

.response-card-container:hover {
  transform: translateY(-2px);
}

.text-h5 {
  font-size: 16px !important;
  font-weight: 500;
  color: #333;
}

.v-card-subtitle {
  color: #666;
}

.response-card-image {
  border-radius: 8px;
  overflow: hidden;
  max-height: 180px;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.response-card-image:hover {
  transform: scale(1.02);
}

.response-button-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding: 8px 0;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  margin-left: 10px !important;
  background: transparent !important;
}

.v-card-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
}

.response-button {
  background: #82b1ff !important;
  color: #000000 !important;
  border-radius: 20px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
  padding: 0 12px !important;
  height: 32px !important;
  margin: 4px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  position: relative;
  overflow: hidden;
  border: none !important;
  max-width: 100%;
  flex: 0 0 auto;
  box-sizing: border-box;
  font-size: 0.875rem !important;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.response-button:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.response-button:hover {
  background: #6889ff !important;
  color: #000000 !important;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2) !important;
  transform: translateY(-2px) !important;
}

.response-button:hover:after {
  transform: translateX(100%);
}

.v-btn--disabled.response-button {
  opacity: 0.26 !important;
  background-color: #E0EBFD !important;
  color: #000 !important;
}

.response-link-button {
  background: rgba(130, 177, 255, 0.1) !important;
  color: #000000 !important;
  border: 1px solid #82b1ff !important;
  border-radius: 20px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05) !important;
  padding: 0 14px !important;
  height: 32px !important;
  font-size: 0.875rem !important;
}

.response-link-button:hover {
  background: rgba(130, 177, 255, 0.2) !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px) !important;
}

@media (max-width: 480px) {
  .response-card-container {
    max-width: 280px;
  }
  
  .response-button {
    padding: 0 10px !important;
    height: 30px !important;
    font-size: 0.8rem !important;
  }
  
  .response-card-image {
    max-height: 150px;
  }
}
</style>
